import {
  ActionIcon,
  Checkbox,
  Combobox,
  ComboboxItem,
  Group,
  Loader,
  Pagination,
  ScrollArea,
  TextInput,
  TextInputProps,
  useCombobox,
} from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './transfer-list.module.css';

export interface RenderListProps {
  options: ComboboxItem[];
  onTransfer: (options: string[]) => void;
  type: 'forward' | 'backward';
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?(type: 'forward' | 'backward', search: string): void;
  loading?: boolean;
  onPageChange?(page: number): void;
  pages?: number;
}

export function RenderList({
  options,
  onTransfer,
  type,
  searchable = true,
  searchPlaceholder,
  onSearch,
  loading = false,
  onPageChange,
  pages,
}: RenderListProps) {
  const { t } = useTranslation();
  const combobox = useCombobox();
  const [value, setValue] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const handleSearch: TextInputProps['onChange'] = (event) => {
    if (onSearch) {
      onSearch(type, event.currentTarget.value);
    } else {
      setSearch(event.currentTarget.value);
      combobox.updateSelectedOptionIndex();
    }
  };

  const handleValueSelect = (val: string) =>
    setValue((current) =>
      current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val],
    );

  const items = options
    .filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase().trim()),
    )
    .map((item) => (
      <Combobox.Option
        active={value.includes(item.value)}
        className={classes.option}
        key={item.value}
        onMouseOver={() => combobox.resetSelectedOption()}
        value={item.value}
      >
        <Group gap="sm">
          <Checkbox
            aria-hidden
            checked={value.includes(item.value)}
            readOnly
            style={{ pointerEvents: 'none' }}
            tabIndex={-1}
          />
          <span>{item.label}</span>
        </Group>
      </Combobox.Option>
    ));

  return (
    <div className={classes.renderList} data-type={type}>
      <Combobox onOptionSubmit={handleValueSelect} store={combobox}>
        <Combobox.EventsTarget>
          <Group className={classes.controls} gap={0} wrap="nowrap">
            <TextInput
              classNames={{
                root: classes.inputRoot,
                input: classes.input,
              }}
              disabled={!searchable}
              onChange={handleSearch}
              placeholder={searchPlaceholder || t('common.search')}
              radius="xs"
              rightSection={loading ? <Loader size={18} /> : null}
              value={!onSearch ? search : undefined}
            />
            <ActionIcon
              className={classes.control}
              color="pf-green"
              onClick={() => {
                onTransfer(value);
                setValue([]);
              }}
              radius={0}
              size={36}
              variant="filled"
            >
              <IconChevronRight className={classes.icon} />
            </ActionIcon>
          </Group>
        </Combobox.EventsTarget>

        <div className={classes.list}>
          <ScrollArea h={300} type="scroll">
            <Combobox.Options>
              {items.length > 0 ? items : null}
            </Combobox.Options>
          </ScrollArea>
        </div>
        {onPageChange && pages ? (
          <Pagination mt="sm" onChange={onPageChange} total={pages} w="100%" />
        ) : null}
      </Combobox>
    </div>
  );
}

export default RenderList;
